import React, { useRef, useState, useEffect } from 'react';
import Button from '../components/ui/Button';
import Icon   from '../components/ui/Icon';
import { useAuth } from '../context/AuthContext';
import { useVerifyOtp, useResendOtp } from '../hooks/useVerifyOtp';
import { useToast } from '../context/ToastContext';

const OTP_LENGTH = 6;

const OtpPage = () => {
  const { goTo, pendingData } = useAuth();
  const verifyMutation = useVerifyOtp();
  const resendMutation = useResendOtp();

  const { error: toastError } = useToast();
  const [otp,   setOtp]   = useState(Array(OTP_LENGTH).fill(''));
  const [timer, setTimer] = useState(300);
  const refs = useRef([]);

  useEffect(() => {
    if (timer <= 0) return;
    const t = setTimeout(() => setTimer((p) => p - 1), 1000);
    return () => clearTimeout(t);
  }, [timer]);

  const handleKey = (e, idx) => {
    const { key } = e;
    if (key === 'Backspace') {
      if (otp[idx]) {
        const next = [...otp]; next[idx] = ''; setOtp(next);
      } else if (idx > 0) { refs.current[idx - 1]?.focus(); }
      return;
    }
    if (!/^\d$/.test(key)) return;
    const next = [...otp]; next[idx] = key; setOtp(next);
    if (idx < OTP_LENGTH - 1) refs.current[idx + 1]?.focus();
  };

  const handlePaste = (e) => {
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!text) return;
    const next = Array(OTP_LENGTH).fill('');
    text.split('').forEach((ch, i) => { next[i] = ch; });
    setOtp(next);
    refs.current[Math.min(text.length, OTP_LENGTH - 1)]?.focus();
    e.preventDefault();
  };

  const handleVerify = () => {
    const code = otp.join('');
    if (code.length < OTP_LENGTH) { toastError('Enter the complete OTP'); return; }
    verifyMutation.mutate(code, {
      onError: () => {
        setOtp(Array(OTP_LENGTH).fill(''));
        refs.current[0]?.focus();
      },
    });
  };

  const handleResend = () => {
    setOtp(Array(OTP_LENGTH).fill(''));
    resendMutation.mutate(undefined, {
      onSuccess: () => setTimer(300),
      onSettled: () => refs.current[0]?.focus(),
    });
  };

  const maskedContact = pendingData?.email
    ? pendingData.email.replace(/(.{2}).+(@.+)/, '$1***$2')
    : pendingData?.phone || '••••••••••';

  const formatTimer = () =>
    `${String(Math.floor(timer / 60)).padStart(2, '0')}:${String(timer % 60).padStart(2, '0')}`;

  return (
    <div className="page-scroll flex flex-col min-h-full bg-surface-900 relative overflow-hidden">
      <div className="noise-overlay" />
      <div
        className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 rounded-full opacity-[0.05] pointer-events-none"
        style={{ background: 'radial-gradient(circle, #6366f1, transparent 70%)' }}
      />

      <div className="px-6 pt-14 pb-6 animate-fadeUp" style={{ animationFillMode: 'forwards' }}>
        <button
          onClick={() => goTo('add-property')}
          className="w-9 h-9 rounded-xl bg-surface-700 flex items-center justify-center text-surface-200 hover:text-slate-800 mb-6 press-scale border border-surface-400"
        >
          <Icon name="arrowLeft" size={17} />
        </button>
      </div>

      <div className="flex-1 flex flex-col items-center px-6">
        <div
          className="w-20 h-20 rounded-3xl flex items-center justify-center mb-6 brand-glow animate-scaleIn"
          style={{ animationFillMode: 'forwards', background: 'linear-gradient(135deg, #4338ca 0%, #6366f1 60%, #818cf8 100%)' }}
        >
          <span className="text-3xl">🔐</span>
        </div>

        <h1 className="font-syne font-extrabold text-slate-800 text-[1.9rem] text-center leading-tight mb-2 animate-fadeUp s2" style={{ animationFillMode: 'forwards' }}>
          Verify Email
        </h1>
        <p className="font-dm text-surface-200 text-sm text-center mb-8 animate-fadeUp s3" style={{ animationFillMode: 'forwards' }}>
          We sent a 6-digit code to your email<br />
          <span className="text-slate-800 font-medium">{maskedContact}</span>
        </p>

        <div className="flex gap-3 mb-3 animate-fadeUp s3" style={{ animationFillMode: 'forwards' }} onPaste={handlePaste}>
          {otp.map((digit, idx) => (
            <input
              key={idx}
              ref={(el) => (refs.current[idx] = el)}
              type="tel" inputMode="numeric" maxLength={1} value={digit}
              onKeyDown={(e) => handleKey(e, idx)} onChange={() => {}}
              className={`otp-input ${digit ? 'filled' : ''}`}
              autoFocus={idx === 0}
            />
          ))}
        </div>

        <div className="w-full mt-4 animate-fadeUp s4" style={{ animationFillMode: 'forwards' }}>
          <Button size="lg" loading={verifyMutation.isPending} onClick={handleVerify}>
            <Icon name="check" size={16} />
            Verify &amp; Continue
          </Button>
        </div>

        <div className="mt-6 animate-fadeUp s5" style={{ animationFillMode: 'forwards' }}>
          {timer > 0 ? (
            <p className="font-dm text-sm text-surface-200">
              Resend OTP in{' '}
              <span className="text-brand-600 font-semibold font-syne">{formatTimer()}</span>
            </p>
          ) : (
            <button
              onClick={handleResend}
              disabled={resendMutation.isPending}
              className="font-dm text-sm text-brand-600 font-semibold hover:text-brand-500 transition-colors press-scale disabled:opacity-50"
            >
              {resendMutation.isPending ? 'Sending…' : 'Resend OTP'}
            </button>
          )}
        </div>
      </div>

      <div className="h-12" />
    </div>
  );
};

export default OtpPage;
